const STORAGE_KEY = "hooligans.guest-favorites.v1";
const ITEMS_STORAGE_KEY = "hooligans.guest-favorites.items.v1";

export type GuestWishlistItem = {
  id: string;
  productId: string;
  name: string;
  model?: string;
  href?: string | null;
  url?: string | null;
  image?: string | null;
  imageLazy?: string | null;
  images?: Array<{ image?: string; imageLazy?: string; imageBig?: string }>;
  price: string;
  priceRaw?: number | null;
  previousPriceRaw?: number | null;
  previousPriceFormatted?: string | null;
  priceChanged?: boolean;
  special?: string;
  inStock: boolean;
  stockStatus: string;
  available: boolean;
  minimum: number;
  requiresOptions: boolean;
  cartEligible: boolean;
  lowStock?: boolean;
  finalSale?: boolean;
};

export function hasGuestWishlistSelection(): boolean {
  if (typeof window === "undefined") return false;
  try {
    return window.localStorage.getItem(STORAGE_KEY) !== null;
  } catch {
    return false;
  }
}

export function readGuestWishlistIds(): string[] {
  if (typeof window === "undefined") return [];
  try {
    const value = JSON.parse(window.localStorage.getItem(STORAGE_KEY) || "[]");
    return Array.isArray(value)
      ? Array.from(
          new Set(value.map(String).filter((id) => /^[1-9][0-9]*$/.test(id)))
        )
      : [];
  } catch {
    return [];
  }
}

export function writeGuestWishlistIds(ids: Array<string | number>): void {
  if (typeof window === "undefined") return;
  const normalized = Array.from(
    new Set(ids.map(String).filter((id) => /^[1-9][0-9]*$/.test(id)))
  );
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(normalized));
  } catch {
    // A blocked storage API must not prevent server-session favorites.
  }
}

export function clearGuestWishlistIds(): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.removeItem(STORAGE_KEY);
    window.localStorage.removeItem(ITEMS_STORAGE_KEY);
  } catch {
    // The authenticated server wishlist remains authoritative.
  }
}

function validId(value: unknown): string | null {
  const id = String(value ?? "");
  return /^[1-9][0-9]*$/.test(id) ? id : null;
}

function optionalString(value: unknown): string | null {
  return typeof value === "string" && value.length ? value : null;
}

function normalizeImages(value: unknown): GuestWishlistItem["images"] {
  if (!Array.isArray(value)) return [];
  return value
    .map((image) => {
      if (!image || typeof image !== "object") return null;
      const source = image as Record<string, unknown>;
      const normalized = {
        image: optionalString(source.image) || undefined,
        imageLazy: optionalString(source.imageLazy) || undefined,
        imageBig: optionalString(source.imageBig) || undefined,
      };
      return normalized.image || normalized.imageLazy || normalized.imageBig
        ? normalized
        : null;
    })
    .filter(
      (
        image
      ): image is { image?: string; imageLazy?: string; imageBig?: string } =>
        image !== null
    )
    .slice(0, 3);
}

function normalizeItem(value: unknown): GuestWishlistItem | null {
  if (!value || typeof value !== "object") return null;
  const item = value as Record<string, unknown>;
  const id = validId(item.id ?? item.productId);
  if (!id) return null;
  const name = String(item.name ?? "").trim();
  if (!name) return null;
  const minimum = Math.max(1, Number(item.minimum ?? 1) || 1);
  const inStock = item.inStock !== false;
  const requiresOptions = item.requiresOptions === true;
  return {
    id,
    productId: id,
    name,
    model: optionalString(item.model) || undefined,
    href: optionalString(item.href),
    url: optionalString(item.url),
    image: optionalString(item.image),
    imageLazy: optionalString(item.imageLazy),
    images: normalizeImages(item.images),
    price: String(item.price ?? ""),
    priceRaw:
      typeof item.priceRaw === "number" && Number.isFinite(item.priceRaw)
        ? item.priceRaw
        : null,
    previousPriceRaw:
      typeof item.previousPriceRaw === "number" &&
      Number.isFinite(item.previousPriceRaw)
        ? item.previousPriceRaw
        : null,
    previousPriceFormatted: optionalString(item.previousPriceFormatted),
    priceChanged: item.priceChanged === true,
    special: optionalString(item.special) || undefined,
    inStock,
    stockStatus:
      optionalString(item.stockStatus) ||
      (inStock ? "In stock" : "Out of stock"),
    available: item.available !== false,
    minimum,
    requiresOptions,
    cartEligible: item.cartEligible === true,
    lowStock: item.lowStock === true,
    finalSale: item.finalSale === true,
  };
}

export function readGuestWishlistItems(): GuestWishlistItem[] {
  if (typeof window === "undefined") return [];
  try {
    const value = JSON.parse(
      window.localStorage.getItem(ITEMS_STORAGE_KEY) || "[]"
    );
    if (!Array.isArray(value)) return [];
    const seen = new Set<string>();
    return value.reduce<GuestWishlistItem[]>((items, candidate) => {
      const item = normalizeItem(candidate);
      if (item && !seen.has(item.id)) {
        seen.add(item.id);
        items.push(item);
      }
      return items;
    }, []);
  } catch {
    return [];
  }
}

export function writeGuestWishlistItems(items: GuestWishlistItem[]): void {
  if (typeof window === "undefined") return;
  const normalized = items
    .map(normalizeItem)
    .filter((item): item is GuestWishlistItem => item !== null);
  try {
    window.localStorage.setItem(ITEMS_STORAGE_KEY, JSON.stringify(normalized));
  } catch {
    // A blocked storage API must not prevent in-memory guest favorites.
  }
}
